/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Data Integrity Scanner Page
 * ============================================================================
 *
 * Real-time data integrity scanning dashboard for SuperAdmin.
 * Detects billing anomalies, subscription inconsistencies, and data issues.
 * Features animated health gauge, severity breakdown, and categorized results.
 *
 * ============================================================================
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  AlertTriangle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Scan,
  ExternalLink,
  Zap,
  Clock,
  CheckCircle2,
  XCircle,
  Building2,
  FileText,
  CreditCard,
  Database,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDataIntegrityScan } from '@/hooks/api/useDataIntegrity';
import type { DataAnomaly, DataIntegrityScanResult } from '@/services/superadmin/data-integrity.service';

// ============================================================================
// Category config
// ============================================================================

const CATEGORY_CONFIG: Record<string, { label: string; icon: typeof ShieldCheck; color: string }> = {
  BILLING: { label: 'Billing Integrity', icon: CreditCard, color: 'var(--color-warning)' },
  SUBSCRIPTION: { label: 'Subscription Health', icon: Database, color: 'var(--color-info)' },
  ORGANIZATION: { label: 'Organization Integrity', icon: Building2, color: 'var(--color-brand-primary)' },
  DATA_INTEGRITY: { label: 'Data Consistency', icon: FileText, color: 'var(--color-success)' },
};

const SEVERITY_CONFIG: Record<string, { label: string; icon: typeof AlertCircle; cssClass: string }> = {
  CRITICAL: { label: 'Critical', icon: XCircle, cssClass: 'di-severity--critical' },
  WARNING: { label: 'Warning', icon: AlertTriangle, cssClass: 'di-severity--warning' },
  INFO: { label: 'Info', icon: Info, cssClass: 'di-severity--info' },
};

// ============================================================================
// Component
// ============================================================================

export default function DataIntegrityPage() {
  const navigate = useNavigate();
  const [scanTriggered, setScanTriggered] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const { data, isLoading, isFetching, refetch } = useDataIntegrityScan(scanTriggered);

  // Animate health score on load
  useEffect(() => {
    if (!data) return;
    setAnimatedScore(0);
    const target = data.healthScore;
    const duration = 1200;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    // Stagger-reveal results
    const timer = setTimeout(() => setShowResults(true), 400);
    return () => clearTimeout(timer);
  }, [data]);

  // Auto-expand categories with critical issues
  useEffect(() => {
    if (!data) return;
    const expanded: Record<string, boolean> = {};
    for (const [category, anomalies] of Object.entries(data.anomaliesByCategory)) {
      expanded[category] = anomalies.some(a => a.severity === 'CRITICAL');
    }
    setExpandedCategories(expanded);
  }, [data]);

  const handleScan = useCallback(() => {
    setScanTriggered(true);
    setShowResults(false);
    refetch();
  }, [refetch]);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  }, []);

  const navigateToEntity = useCallback((anomaly: DataAnomaly) => {
    const meta = anomaly.metadata;
    if (meta.invoiceId) {
      navigate(`/superadmin/invoices/${meta.invoiceId}`);
    } else if (meta.orgId || anomaly.orgId) {
      navigate(`/superadmin/organizations/${meta.orgId || anomaly.orgId}`);
    }
  }, [navigate]);

  const isScanning = isLoading || isFetching;

  // Sort categories: ones with critical issues first
  const sortedCategories = useMemo(() => {
    if (!data) return [];
    const allCats = ['BILLING', 'SUBSCRIPTION', 'ORGANIZATION', 'DATA_INTEGRITY'];
    return allCats.map(cat => ({
      key: cat,
      anomalies: data.anomaliesByCategory[cat] || [],
      config: CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.DATA_INTEGRITY,
    }));
  }, [data]);

  return (
    <div className="di-page">
      {/* Header */}
      <div className="di-header">
        <div className="di-header__title-row">
          <div className="di-header__title-group">
            <ShieldCheck className="di-header__icon" />
            <div>
              <h1 className="di-header__title">Data Integrity Scanner</h1>
              <p className="di-header__subtitle">
                Detect anomalies, inconsistencies, and data issues across billing and subscriptions
              </p>
            </div>
          </div>
          <button
            className={`di-scan-btn ${isScanning ? 'di-scan-btn--scanning' : ''}`}
            onClick={handleScan}
            disabled={isScanning}
          >
            {isScanning ? (
              <>
                <Scan className="di-scan-btn__icon di-scan-btn__icon--spinning" />
                <span>Scanning...</span>
              </>
            ) : (
              <>
                <Zap className="di-scan-btn__icon" />
                <span>{data ? 'Re-scan' : 'Run Scan'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Pre-scan state */}
      {!data && !isScanning && (
        <div className="di-prescan">
          <div className="di-prescan__visual">
            <div className="di-prescan__rings">
              <div className="di-prescan__ring di-prescan__ring--1" />
              <div className="di-prescan__ring di-prescan__ring--2" />
              <div className="di-prescan__ring di-prescan__ring--3" />
              <ShieldCheck className="di-prescan__shield" />
            </div>
          </div>
          <h2 className="di-prescan__title">Ready to Scan</h2>
          <p className="di-prescan__desc">
            Run a comprehensive scan across 18 integrity checks covering billing, subscriptions,
            organization data, and cross-table consistency.
          </p>
          <button className="di-scan-btn di-scan-btn--large" onClick={handleScan}>
            <Zap className="di-scan-btn__icon" />
            <span>Start Integrity Scan</span>
          </button>
        </div>
      )}

      {/* Scanning animation */}
      {isScanning && !data && (
        <div className="di-scanning">
          <div className="di-scanning__pulse" />
          <Scan className="di-scanning__icon" />
          <p className="di-scanning__text">Analyzing data integrity...</p>
          <div className="di-scanning__bar">
            <div className="di-scanning__bar-fill" />
          </div>
        </div>
      )}

      {/* Results */}
      {data && (
        <>
          {/* Health Score + Summary Cards */}
          <div className="di-summary">
            <HealthGauge score={animatedScore} label={data.healthLabel} />
            <div className="di-summary__cards">
              <SeverityCard
                severity="CRITICAL"
                count={data.countBySeverity.CRITICAL || 0}
                delay={0}
                visible={showResults}
              />
              <SeverityCard
                severity="WARNING"
                count={data.countBySeverity.WARNING || 0}
                delay={1}
                visible={showResults}
              />
              <SeverityCard
                severity="INFO"
                count={data.countBySeverity.INFO || 0}
                delay={2}
                visible={showResults}
              />
              <ScanMeta result={data} delay={3} visible={showResults} />
            </div>
          </div>

          {/* Category sections */}
          <div className="di-categories">
            {sortedCategories.map((cat, idx) => (
              <CategorySection
                key={cat.key}
                category={cat.key}
                config={cat.config}
                anomalies={cat.anomalies}
                expanded={!!expandedCategories[cat.key]}
                onToggle={() => toggleCategory(cat.key)}
                onNavigate={navigateToEntity}
                delay={idx}
                visible={showResults}
              />
            ))}
          </div>
        </>
      )}

      <style>{styles}</style>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function HealthGauge({ score, label }: { score: number; label: string }) {
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 95 ? 'var(--color-success)' : score >= 80 ? 'var(--color-info)' : score >= 60 ? 'var(--color-warning)' : 'var(--color-error)';
  const GaugeIcon = score >= 80 ? ShieldCheck : score >= 60 ? ShieldAlert : ShieldX;

  return (
    <div className="di-gauge">
      <div className="di-gauge__ring-wrapper">
        <svg className="di-gauge__svg" viewBox="0 0 120 120">
          <circle className="di-gauge__track" cx="60" cy="60" r="54" />
          <circle
            className="di-gauge__fill"
            cx="60" cy="60" r="54"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
              stroke: color,
            }}
          />
        </svg>
        <div className="di-gauge__center">
          <span className="di-gauge__score" style={{ color }}>{score}</span>
          <span className="di-gauge__percent">%</span>
        </div>
      </div>
      <div className="di-gauge__label-row">
        <GaugeIcon style={{ color, width: 16, height: 16 }} />
        <span className="di-gauge__label" style={{ color }}>{label}</span>
      </div>
      <span className="di-gauge__subtitle">Health Score</span>
    </div>
  );
}

function SeverityCard({ severity, count, delay, visible }: {
  severity: string; count: number; delay: number; visible: boolean;
}) {
  const cfg = SEVERITY_CONFIG[severity];
  const Icon = cfg.icon;
  return (
    <div
      className={`di-scard ${cfg.cssClass} ${count > 0 && severity === 'CRITICAL' ? 'di-scard--pulse' : ''}`}
      style={{ animationDelay: `${delay * 0.08}s`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(12px)' }}
    >
      <Icon className="di-scard__icon" />
      <span className="di-scard__count">{count}</span>
      <span className="di-scard__label">{cfg.label}</span>
    </div>
  );
}

function ScanMeta({ result, delay, visible }: {
  result: DataIntegrityScanResult; delay: number; visible: boolean;
}) {
  return (
    <div
      className="di-scard di-scard--meta"
      style={{ animationDelay: `${delay * 0.08}s`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(12px)' }}
    >
      <Clock className="di-scard__icon" />
      <span className="di-scard__count">{result.scanDurationMs}ms</span>
      <span className="di-scard__label">18 checks run</span>
    </div>
  );
}

function CategorySection({ category, config, anomalies, expanded, onToggle, onNavigate, delay, visible }: {
  category: string;
  config: { label: string; icon: typeof ShieldCheck; color: string };
  anomalies: DataAnomaly[];
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (a: DataAnomaly) => void;
  delay: number;
  visible: boolean;
}) {
  const Icon = config.icon;
  const hasCritical = anomalies.some(a => a.severity === 'CRITICAL');
  const hasWarning = anomalies.some(a => a.severity === 'WARNING');
  const count = anomalies.length;

  return (
    <div
      className={`di-cat ${hasCritical ? 'di-cat--critical' : hasWarning ? 'di-cat--warning' : ''}`}
      style={{ animationDelay: `${delay * 0.1 + 0.2}s`, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)' }}
    >
      <button className="di-cat__header" onClick={onToggle}>
        <div className="di-cat__header-left">
          <Icon style={{ color: config.color, width: 18, height: 18 }} />
          <span className="di-cat__title">{config.label}</span>
          {count === 0 ? (
            <span className="di-cat__badge di-cat__badge--clean">
              <CheckCircle2 style={{ width: 12, height: 12 }} /> Clean
            </span>
          ) : (
            <span className={`di-cat__badge ${hasCritical ? 'di-cat__badge--critical' : hasWarning ? 'di-cat__badge--warning' : 'di-cat__badge--info'}`}>
              {count} {count === 1 ? 'issue' : 'issues'}
            </span>
          )}
        </div>
        {count > 0 && (
          expanded ? <ChevronDown className="di-cat__chevron" /> : <ChevronRight className="di-cat__chevron" />
        )}
      </button>

      {expanded && count > 0 && (
        <div className="di-cat__body">
          {anomalies.map((anomaly, i) => (
            <AnomalyRow key={anomaly.id} anomaly={anomaly} index={i} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function AnomalyRow({ anomaly, index, onNavigate }: {
  anomaly: DataAnomaly; index: number; onNavigate: (a: DataAnomaly) => void;
}) {
  const cfg = SEVERITY_CONFIG[anomaly.severity];
  const SevIcon = cfg.icon;
  const hasLink = !!(anomaly.metadata.invoiceId || anomaly.metadata.orgId || anomaly.orgId);

  return (
    <div className="di-anomaly" style={{ animationDelay: `${index * 0.04}s` }}>
      <div className="di-anomaly__header">
        <div className={`di-anomaly__severity ${cfg.cssClass}`}>
          <SevIcon className="di-anomaly__sev-icon" />
          <span>{cfg.label}</span>
        </div>
        <span className="di-anomaly__title">{anomaly.title}</span>
      </div>
      <p className="di-anomaly__desc">{anomaly.description}</p>
      <div className="di-anomaly__footer">
        <div className="di-anomaly__action">
          <RefreshCw className="di-anomaly__action-icon" />
          <span>{anomaly.suggestedAction}</span>
        </div>
        {hasLink && (
          <button className="di-anomaly__link" onClick={() => onNavigate(anomaly)}>
            <ExternalLink style={{ width: 13, height: 13 }} />
            <span>View</span>
          </button>
        )}
      </div>
      {anomaly.orgName && (
        <span className="di-anomaly__org">{anomaly.orgName}</span>
      )}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = `
/* Page layout */
.di-page {
  display: flex; flex-direction: column; gap: 1.5rem;
  padding: 1.5rem 2rem; width: 100%; box-sizing: border-box;
  min-height: 100%;
}

/* Header */
.di-header {
  animation: di-slide-down 0.4s cubic-bezier(0.22,1,0.36,1) both;
}
.di-header__title-row {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
  flex-wrap: wrap;
}
.di-header__title-group {
  display: flex; align-items: flex-start; gap: 0.75rem;
}
.di-header__icon {
  width: 28px; height: 28px; color: var(--color-brand-primary);
  margin-top: 2px; flex-shrink: 0;
}
.di-header__title {
  font-family: var(--font-heading); font-size: 1.5rem; font-weight: 700;
  color: var(--color-text-primary); margin: 0; line-height: 1.3;
}
.di-header__subtitle {
  color: var(--color-text-secondary); font-size: 0.85rem; margin: 0.15rem 0 0;
}

/* Scan button */
.di-scan-btn {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.6rem 1.25rem; border-radius: var(--radius-md);
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  border: none; color: #fff;
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-primary-hover));
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: all 0.2s ease;
}
.di-scan-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
}
.di-scan-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.di-scan-btn--large { padding: 0.75rem 2rem; font-size: 0.95rem; }
.di-scan-btn__icon { width: 16px; height: 16px; }
.di-scan-btn__icon--spinning { animation: di-spin 1s linear infinite; }

/* Pre-scan state */
.di-prescan {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; text-align: center;
  padding: 4rem 2rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  animation: di-slide-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.1s both;
}
.di-prescan__visual { margin-bottom: 1.5rem; }
.di-prescan__rings {
  position: relative; width: 120px; height: 120px;
  display: flex; align-items: center; justify-content: center;
}
.di-prescan__ring {
  position: absolute; border-radius: 50%;
  border: 2px solid var(--color-brand-primary);
  opacity: 0.15;
  animation: di-ring-pulse 3s ease-in-out infinite;
}
.di-prescan__ring--1 { width: 80px; height: 80px; animation-delay: 0s; }
.di-prescan__ring--2 { width: 100px; height: 100px; animation-delay: 0.5s; }
.di-prescan__ring--3 { width: 120px; height: 120px; animation-delay: 1s; }
.di-prescan__shield {
  width: 36px; height: 36px; color: var(--color-brand-primary);
  position: relative; z-index: 1;
}
.di-prescan__title {
  font-family: var(--font-heading); font-size: 1.25rem; font-weight: 700;
  color: var(--color-text-primary); margin: 0 0 0.5rem;
}
.di-prescan__desc {
  color: var(--color-text-secondary); max-width: 420px;
  margin: 0 0 1.5rem; font-size: 0.88rem; line-height: 1.6;
}

/* Scanning animation */
.di-scanning {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 4rem 2rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
}
.di-scanning__pulse {
  width: 80px; height: 80px; border-radius: 50%;
  background: var(--color-brand-primary); opacity: 0.1;
  animation: di-scan-pulse 1.5s ease-in-out infinite;
  position: absolute;
}
.di-scanning__icon {
  width: 32px; height: 32px; color: var(--color-brand-primary);
  animation: di-spin 2s linear infinite;
  margin-bottom: 1rem; position: relative; z-index: 1;
}
.di-scanning__text {
  color: var(--color-text-secondary); font-size: 0.9rem; margin: 0 0 1.5rem;
}
.di-scanning__bar {
  width: 240px; height: 4px; background: var(--color-bg-tertiary);
  border-radius: 2px; overflow: hidden;
}
.di-scanning__bar-fill {
  height: 100%; width: 30%;
  background: linear-gradient(90deg, var(--color-brand-primary), var(--color-info));
  border-radius: 2px;
  animation: di-bar-sweep 1.5s ease-in-out infinite;
}

/* Summary row */
.di-summary {
  display: flex; gap: 1.5rem; align-items: stretch;
  animation: di-slide-up 0.5s cubic-bezier(0.22,1,0.36,1) 0.05s both;
}
.di-summary__cards {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem; flex: 1;
}

/* Health gauge */
.di-gauge {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 0.35rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: 1.5rem 2rem; min-width: 180px;
  box-shadow: var(--shadow-sm);
}
.di-gauge__ring-wrapper {
  position: relative; width: 120px; height: 120px;
}
.di-gauge__svg {
  width: 120px; height: 120px; transform: rotate(-90deg);
}
.di-gauge__track {
  fill: none; stroke: var(--color-bg-tertiary); stroke-width: 8;
}
.di-gauge__fill {
  fill: none; stroke-width: 8; stroke-linecap: round;
  transition: stroke-dashoffset 1.2s cubic-bezier(0.22,1,0.36,1);
}
.di-gauge__center {
  position: absolute; inset: 0; display: flex;
  align-items: center; justify-content: center;
}
.di-gauge__score {
  font-family: var(--font-heading); font-size: 2rem; font-weight: 800;
  line-height: 1;
}
.di-gauge__percent {
  font-size: 0.85rem; font-weight: 600; color: var(--color-text-muted);
  margin-top: 4px;
}
.di-gauge__label-row {
  display: flex; align-items: center; gap: 0.35rem;
}
.di-gauge__label {
  font-size: 0.85rem; font-weight: 700;
}
.di-gauge__subtitle {
  font-size: 0.7rem; color: var(--color-text-muted); text-transform: uppercase;
  letter-spacing: 0.08em; font-weight: 600;
}

/* Severity cards */
.di-scard {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 0.2rem;
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: 1rem; text-align: center;
  box-shadow: var(--shadow-sm);
  transition: all 0.4s cubic-bezier(0.22,1,0.36,1);
}
.di-scard__icon { width: 20px; height: 20px; }
.di-scard__count {
  font-family: var(--font-heading); font-size: 1.5rem; font-weight: 800;
  line-height: 1.1;
}
.di-scard__label {
  font-size: 0.72rem; color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;
}

/* Severity colors */
.di-severity--critical .di-scard__icon,
.di-severity--critical .di-scard__count { color: var(--color-error); }
.di-severity--critical { border-color: color-mix(in srgb, var(--color-error) 20%, transparent); }

.di-severity--warning .di-scard__icon,
.di-severity--warning .di-scard__count { color: var(--color-warning); }
.di-severity--warning { border-color: color-mix(in srgb, var(--color-warning) 20%, transparent); }

.di-severity--info .di-scard__icon,
.di-severity--info .di-scard__count { color: var(--color-info); }
.di-severity--info { border-color: color-mix(in srgb, var(--color-info) 20%, transparent); }

.di-scard--meta .di-scard__icon { color: var(--color-text-muted); }
.di-scard--meta .di-scard__count { color: var(--color-text-secondary); font-size: 1.1rem; }

/* Critical pulse */
.di-scard--pulse {
  animation: di-critical-glow 2s ease-in-out infinite;
}

/* Category sections */
.di-categories {
  display: flex; flex-direction: column; gap: 0.75rem;
}
.di-cat {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden; box-shadow: var(--shadow-sm);
  transition: all 0.4s cubic-bezier(0.22,1,0.36,1);
}
.di-cat--critical { border-left: 3px solid var(--color-error); }
.di-cat--warning { border-left: 3px solid var(--color-warning); }

.di-cat__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.85rem 1.25rem; width: 100%; border: none;
  background: none; cursor: pointer; color: var(--color-text-primary);
  font-size: 0.9rem;
}
.di-cat__header:hover { background: var(--color-bg-tertiary); }
.di-cat__header-left {
  display: flex; align-items: center; gap: 0.6rem;
}
.di-cat__title { font-weight: 650; }
.di-cat__badge {
  display: inline-flex; align-items: center; gap: 0.25rem;
  font-size: 0.7rem; font-weight: 600; padding: 0.15rem 0.5rem;
  border-radius: var(--radius-full);
}
.di-cat__badge--clean {
  background: color-mix(in srgb, var(--color-success) 12%, transparent);
  color: var(--color-success);
}
.di-cat__badge--critical {
  background: color-mix(in srgb, var(--color-error) 12%, transparent);
  color: var(--color-error);
}
.di-cat__badge--warning {
  background: color-mix(in srgb, var(--color-warning) 12%, transparent);
  color: var(--color-warning);
}
.di-cat__badge--info {
  background: color-mix(in srgb, var(--color-info) 12%, transparent);
  color: var(--color-info);
}
.di-cat__chevron {
  width: 16px; height: 16px; color: var(--color-text-muted);
  transition: transform 0.2s ease;
}
.di-cat__body {
  border-top: 1px solid var(--color-border-subtle);
  padding: 0.5rem;
}

/* Anomaly rows */
.di-anomaly {
  padding: 0.85rem 1rem; border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-subtle);
  margin-bottom: 0.5rem;
  animation: di-anomaly-in 0.3s cubic-bezier(0.22,1,0.36,1) both;
  position: relative;
}
.di-anomaly:last-child { margin-bottom: 0; }
.di-anomaly__header {
  display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.35rem;
  flex-wrap: wrap;
}
.di-anomaly__severity {
  display: inline-flex; align-items: center; gap: 0.25rem;
  font-size: 0.68rem; font-weight: 700; padding: 0.15rem 0.45rem;
  border-radius: var(--radius-sm); text-transform: uppercase;
  letter-spacing: 0.04em;
}
.di-anomaly__severity.di-severity--critical {
  background: color-mix(in srgb, var(--color-error) 12%, transparent);
  color: var(--color-error);
}
.di-anomaly__severity.di-severity--warning {
  background: color-mix(in srgb, var(--color-warning) 12%, transparent);
  color: var(--color-warning);
}
.di-anomaly__severity.di-severity--info {
  background: color-mix(in srgb, var(--color-info) 12%, transparent);
  color: var(--color-info);
}
.di-anomaly__sev-icon { width: 11px; height: 11px; }
.di-anomaly__title {
  font-weight: 650; font-size: 0.85rem; color: var(--color-text-primary);
}
.di-anomaly__desc {
  font-size: 0.8rem; color: var(--color-text-secondary);
  margin: 0 0 0.5rem; line-height: 1.55;
}
.di-anomaly__footer {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.75rem; flex-wrap: wrap;
}
.di-anomaly__action {
  display: flex; align-items: flex-start; gap: 0.35rem;
  font-size: 0.75rem; color: var(--color-text-muted);
  font-style: italic; line-height: 1.4;
}
.di-anomaly__action-icon {
  width: 12px; height: 12px; flex-shrink: 0; margin-top: 1px;
}
.di-anomaly__link {
  display: inline-flex; align-items: center; gap: 0.3rem;
  font-size: 0.75rem; font-weight: 600;
  color: var(--color-brand-primary); background: none;
  border: 1px solid var(--color-brand-primary);
  border-radius: var(--radius-sm); padding: 0.2rem 0.5rem;
  cursor: pointer; transition: all 0.15s ease;
}
.di-anomaly__link:hover {
  background: var(--color-brand-primary); color: #fff;
}
.di-anomaly__org {
  position: absolute; top: 0.65rem; right: 0.85rem;
  font-size: 0.68rem; color: var(--color-text-muted);
  background: var(--color-bg-tertiary);
  padding: 0.1rem 0.4rem; border-radius: var(--radius-sm);
}

/* Animations */
@keyframes di-slide-down {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes di-slide-up {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes di-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes di-ring-pulse {
  0%, 100% { transform: scale(1); opacity: 0.15; }
  50% { transform: scale(1.08); opacity: 0.3; }
}
@keyframes di-scan-pulse {
  0%, 100% { transform: scale(1); opacity: 0.1; }
  50% { transform: scale(2); opacity: 0; }
}
@keyframes di-bar-sweep {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}
@keyframes di-critical-glow {
  0%, 100% { box-shadow: var(--shadow-sm); }
  50% { box-shadow: 0 0 12px color-mix(in srgb, var(--color-error) 25%, transparent); }
}
@keyframes di-anomaly-in {
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
}

/* Responsive */
@media (max-width: 900px) {
  .di-summary { flex-direction: column; }
  .di-gauge { flex-direction: row; gap: 1rem; padding: 1rem; min-width: unset; }
  .di-gauge__ring-wrapper { width: 80px; height: 80px; }
  .di-gauge__svg { width: 80px; height: 80px; }
  .di-gauge__score { font-size: 1.4rem; }
  .di-summary__cards { grid-template-columns: repeat(4, 1fr); }
}
@media (max-width: 640px) {
  .di-page { padding: 1rem; }
  .di-summary__cards { grid-template-columns: repeat(2, 1fr); }
  .di-header__title-row { flex-direction: column; }
}
@media (prefers-reduced-motion: reduce) {
  .di-header, .di-prescan, .di-scanning__icon, .di-scan-btn__icon--spinning,
  .di-scard--pulse, .di-anomaly, .di-prescan__ring, .di-scanning__pulse,
  .di-scanning__bar-fill { animation: none !important; }
  .di-gauge__fill { transition: none !important; }
}
`;
